import { CHANGE_NOTIFICATION } from '../actions/websocket';
import { EntityType } from '../../utility/types/EntityType';
import { CrudType } from '../../utility/types/CrudType';
import { addDeletedCommentId, fetchComment } from '../slices/conversation/conversationSlice';
import { deleteSingleAlertState, fetchSingleAlert } from '../slices/singleAlertSlice/singleAlertSlice';
import { fetchSavedSearch, removeSavedSearch } from '../slices/savedSearchesSlice/savedSearchesSlice';
import { refreshFilteredAlertList } from '../slices/alertListSlice/alertListSlice';
import { fetchTag, removeTag } from '../slices/tagListSlice/tagListSlice';
import { fetchLink, removeLink } from '../slices/linkListSlice/linkListSlice';
import { refreshFilteredDeviceList } from '../slices/filteredDeviceListSlice/filteredDeviceListSlice';
import { fetchSingleDevice } from '../slices/singleDeviceSlice/singleDeviceSlice';
import { fetchSessionData, fetchUserMessages } from '../slices/sessionSlice';
import {
  fetchChecklistCompletionStats,
  fetchSubscriptionChecklists,
} from '../slices/subscriptionChecklistSlice/subscriptionChecklistSlice';
import {
  fetchNotificationTemplate,
  removeNotificationTemplate,
} from '../slices/notificationTemplatesSlice/notificationTemplatesSlice';
import { fetchQuietTime, removeQuietTime } from '../slices/quietTimesSlice/quietTimesSlice';
import {
  fetchNotificationGroups,
  removeNotificationGroup,
} from '../slices/notificationGroupsSlice/notificationGroupsSlice';
import {
  fetchThresholds,
  removeThresholdRange,
} from '../slices/thresholdRangeSlice/thresholdRangeSlice';

const changeEventMiddleware = () => (store) => (next) => (action) => {
  if (action.type === CHANGE_NOTIFICATION) {
    const {
      entityType, crudType, id, parentType, parentId,
    } = action;
    switch (entityType) {
      case EntityType.ALERT:
        if (store.getState().alertList.alertList.some((alert) => parseInt(alert.id, 10) === id)
            || store.getState().alertList.alertList.length === 0) {
          store.dispatch(refreshFilteredAlertList());
        }
        if (crudType === CrudType.DELETE || crudType === CrudType.CREATE) {
          store.dispatch(fetchSessionData());
        }
        if (store.getState().singleAlert.alert
          && id === parseInt(store.getState().singleAlert.alert.id, 10)) {
          switch (crudType) {
            case CrudType.UPDATE:
              store.dispatch(fetchSingleAlert({ alertId: id }));
              break;
            case CrudType.DELETE:
              store.dispatch(deleteSingleAlertState({ id }));
              break;
            default:
              // eslint-disable-next-line no-console
              console.warn(`Unknown change event crudType: ${crudType}`);
          }
          break;
        }
        break;
      case EntityType.ALERT_COMMENT:
        if (parentType === EntityType.ALERT
          && parentId === parseInt(store.getState().singleAlert.alert.id, 10)) {
          switch (crudType) {
            case CrudType.CREATE:
            case CrudType.UPDATE:
              store.dispatch(fetchComment({ id }));
              break;
            case CrudType.DELETE:
              store.dispatch(addDeletedCommentId({ id }));
              break;
            default:
              // eslint-disable-next-line no-console
              console.warn(`Unknown change event crudType: ${crudType}`);
          }
          break;
        }
        break;
      case EntityType.SAVED_SEARCH:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchSavedSearch(id));
            break;
          case CrudType.DELETE:
            store.dispatch(removeSavedSearch(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.TAG:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(refreshFilteredAlertList());
            store.dispatch(refreshFilteredDeviceList());
            store.dispatch(fetchTag(id));
            break;
          case CrudType.DELETE:
            store.dispatch(removeTag(id));
            store.dispatch(refreshFilteredAlertList());
            store.dispatch(refreshFilteredDeviceList());
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.ROOM_ALERT_LINK:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchLink(id));
            break;
          case CrudType.DELETE:
            store.dispatch(removeLink(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.DEVICE:
        if (store.getState().filteredDeviceList.deviceList.some(
          (device) => parseInt(device.id, 10) === id,
        )) {
          store.dispatch(refreshFilteredDeviceList());
        }
        if (store.getState().singleDevice.device) {
          if (id === parseInt(store.getState().singleDevice.device.general.id, 10)) {
            store.dispatch(fetchSingleDevice({ deviceId: id }));
          }
        }
        break;
      case EntityType.ACCOUNT_MESSAGE:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
          case CrudType.DELETE:
            store.dispatch(fetchUserMessages());
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.NOTIFICATION_TEMPLATE:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchNotificationTemplate(id));
            break;
          case CrudType.DELETE:
            store.dispatch(removeNotificationTemplate(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.SUBSCRIPTION_CHECKLIST:
      case EntityType.SUBSCRIPTION_CHECKLIST_ENTRY:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
          case CrudType.DELETE:
            store.dispatch(fetchSubscriptionChecklists());
            store.dispatch(fetchChecklistCompletionStats());
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.QUIET_TIME:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchQuietTime(id));
            break;
          case CrudType.DELETE:
            store.dispatch(removeQuietTime(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.NOTIFICATION_GROUP:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchNotificationGroups());
            break;
          case CrudType.DELETE:
            store.dispatch(removeNotificationGroup(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      case EntityType.THRESHOLD_RANGE:
        switch (crudType) {
          case CrudType.CREATE:
          case CrudType.UPDATE:
            store.dispatch(fetchThresholds());
            break;
          case CrudType.DELETE:
            store.dispatch(removeThresholdRange(id));
            break;
          default:
            // eslint-disable-next-line no-console
            console.warn(`Unknown change event crudType: ${crudType}`);
        }
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(`Unknown change event crudType: ${crudType}`);
    }
  }

  // Continue with the next middleware or the reducer
  return next(action);
};

export { changeEventMiddleware };

export default changeEventMiddleware();
